/* B variant: real bundle prices/plans, validated flavor counts as line-item properties. */
(() => {
  'use strict';
  if (customElements.get('chura-custom-order')) return;

  class ChuraCustomOrder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector('[data-order-data]');
      if (!source) return;
      try {
        this.config = JSON.parse(source.textContent);
        if (!Array.isArray(this.config.sets)) throw new Error('Missing sets');
      } catch (_) {
        // The server-rendered fallback stays visible with its button disabled.
        return;
      }
      this.initialized = true;
      this.form = this.querySelector('[data-order-form]');
      this.setSelect = this.querySelector('[data-set-select]');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.planSelect = this.querySelector('[data-plan-select]');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.planInput = this.querySelector('[data-plan-input]');
      this.button = this.querySelector('[data-order-submit]');
      this.methods = Array.from(this.querySelectorAll('[data-method]'));
      this.mixer = this.querySelector('chura-flavor-mixer');
      this.mixer?.addEventListener('flavorchange', () => {
        if (!this.changingSet) this.render();
      });
      this.config.sets.forEach((set) => {
        this.addOption(this.setSelect, set.id,
          `${set.label} · ${set.bottles}本${set.productId ? '' : '（セット準備中）'}`);
      });
      if (!this.config.sets.length) {
        this.addOption(this.setSelect, '', 'セット準備中');
        this.setSelect.disabled = true;
      }
      this.setSelect.addEventListener('change', () => this.changeSet());
      this.variantSelect.addEventListener('change', () => this.changeVariant());
      this.planSelect.addEventListener('change', () => this.render());
      this.methods.forEach((radio) => radio.addEventListener('change', () => this.render()));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.querySelector('[data-order-fallback]').hidden = true;
      this.form.hidden = false;
      this.changeSet();
      // Native POST returns can restore a page with a disabled submit button.
      window.addEventListener('pageshow', () => {
        if (this.isConnected) {
          this.submitting = false;
          this.render();
        }
      });
    }

    addOption(select, value, label) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = label;
      select.append(option);
    }

    text(selector, value) {
      this.querySelector(selector).textContent = value || '';
    }

    changeSet() {
      this.currentSet = this.config.sets.find((set) => set.id === this.setSelect.value);
      this.changingSet = true;
      this.mixer?.setSize?.(Number(this.currentSet?.bottles || 0));
      this.changingSet = false;
      this.variantSelect.replaceChildren();
      const variants = this.currentSet?.variants || [];
      variants.forEach((variant) => this.addOption(this.variantSelect, variant.id,
        `${variant.title === 'Default Title' ? '基本セット' : variant.title}${variant.available ? '' : '（売り切れ）'}`));
      const firstAvailable = variants.find((variant) => variant.available);
      if (firstAvailable) this.variantSelect.value = firstAvailable.id;
      this.variantSelect.disabled = !variants.length;
      this.querySelector('[data-variant-field]').hidden = variants.length <= 1;
      this.text('[data-product-title]', this.currentSet?.productTitle);
      this.text('[data-composition]', this.currentSet?.composition);
      this.changeVariant();
    }

    changeVariant() {
      this.currentVariant = this.currentSet?.variants.find((variant) => variant.id === this.variantSelect.value);
      const previousPlanId = this.planSelect.value;
      this.plans = (this.currentVariant?.allocations || []).filter((plan) => plan.recurring === true);
      this.planSelect.replaceChildren();
      this.plans.forEach((plan) => this.addOption(this.planSelect, plan.id, plan.name));
      if (this.plans.some((plan) => plan.id === previousPlanId)) this.planSelect.value = previousPlanId;
      this.planSelect.disabled = !this.plans.length;
      const oneTime = this.methods.find((radio) => radio.value === 'once');
      const subscription = this.methods.find((radio) => radio.value === 'subscription');
      oneTime.disabled = !this.currentVariant || this.currentVariant.requiresSellingPlan;
      subscription.disabled = !this.plans.length;
      if (this.currentVariant?.requiresSellingPlan && this.plans.length) {
        subscription.checked = true;
        oneTime.checked = false;
      }
      // Preserve an explicit subscription choice when an unsupported variant is
      // selected. Do not silently switch the customer into a one-time order.
      this.render();
    }

    get mode() {
      return this.methods.find((radio) => radio.checked)?.value || '';
    }

    get plan() {
      return this.mode === 'subscription'
        ? this.plans.find((plan) => plan.id === this.planSelect.value)
        : undefined;
    }

    flavorState() {
      const state = this.mixer?.getState?.();
      const target = Number(this.currentSet?.bottles);
      const keys = ['plain', 'shikuwasa', 'pineapple', 'roselle'];
      const counts = state?.counts;
      if (!state || !counts || ![7, 14, 21, 28].includes(target)) {
        return { valid: false, message: '味の選択欄を準備しています。' };
      }
      if (keys.some(key => !Number.isSafeInteger(counts[key]) || counts[key] < 0)) {
        return { valid: false, message: '各フレーバーは0以上の整数でお選びください。' };
      }
      const total = keys.reduce((sum, key) => sum + counts[key], 0);
      if (!state.valid || state.target !== target || state.total !== total || total !== target) {
        return { valid: false, message: state.message || `${target}本になるように味の本数を調整してください。` };
      }
      return state;
    }

    priceRow(label, amount) {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const value = document.createElement('dd');
      term.textContent = label;
      value.textContent = amount;
      row.append(term, value);
      this.querySelector('[data-price-details]').append(row);
    }

    showSchedule(plan) {
      const adjustments = plan.adjustments || [];
      if (!adjustments.length) {
        this.priceRow('各回の商品代金', plan.priceMoney);
        return;
      }
      let firstOrder = 1;
      let previousPeriodIsOpen = false;
      adjustments.forEach((adjustment, index) => {
        const count = adjustment.orderCount;
        const fixedPeriod = Number.isInteger(count) && count > 0;
        let label;
        if (previousPeriodIsOpen) {
          label = `価格区分${index + 1}（適用回数はプラン条件をご確認ください）`;
        } else if (fixedPeriod) {
          label = count === 1
            ? (firstOrder === 1 ? '初回の商品代金' : `${firstOrder}回目の商品代金`)
            : `${firstOrder}〜${firstOrder + count - 1}回目の商品代金`;
        } else {
          label = firstOrder === 1 ? '各回の商品代金' : `${firstOrder}回目以降の商品代金`;
        }
        this.priceRow(label, adjustment.priceMoney);
        if (fixedPeriod) firstOrder += count;
        else previousPeriodIsOpen = true;
      });
    }

    blockReason() {
      const variant = this.currentVariant;
      if (!this.currentSet?.productId || !variant) return 'セット準備中';
      if (!this.currentSet.variantsComplete) return 'セットの商品情報を確認中です。';
      if (!variant.available) return '選択したセットは売り切れです。';
      if (variant.quantityMin > 1 || variant.quantityIncrement > 1) return 'このセットは現在、1セットでのご注文に対応していません。';
      if (this.mode === 'subscription' && !this.plan) return '選択した種類は定期購入に対応していません。通常購入をお選びください。';
      if (this.mode !== 'subscription' && variant.requiresSellingPlan) return 'このセットは定期購入専用です。';
      if (this.mode === 'subscription' && !this.config.termsReady) return '定期購入の販売条件を準備しています。';
      const flavors = this.flavorState();
      if (!flavors.valid) return flavors.message;
      if (!this.config.checkoutEnabled) return '現在、セット販売の準備中です。';
      return '';
    }

    render() {
      const subscription = this.mode === 'subscription';
      const variant = this.currentVariant;
      const plan = this.plan;
      this.querySelector('[data-plan-field]').hidden = !subscription;
      this.querySelector('[data-subscription-terms]').hidden = !subscription;
      this.querySelector('[data-price-details]').replaceChildren();
      this.querySelector('[data-plan-description]').hidden = !plan?.description;
      this.text('[data-plan-description]', plan?.description);
      this.text('[data-plan-note]', !variant ? '' : !this.plans.length
        ? 'この種類は定期購入に対応していません。'
        : variant.requiresSellingPlan ? 'このセットは定期購入専用です。' : '定期購入は選択したプランに従って継続してお届けします。');

      this.text('[data-price-label]', subscription ? '今回の商品代金（1セット）' : '商品代金（1セット）');
      this.text('[data-main-price]', subscription
        ? plan?.priceMoney || '定期プラン未対応'
        : variant?.priceMoney || 'セット準備中');
      if (plan) {
        this.showSchedule(plan);
        if (plan.hasMultipleDeliveryPrice) this.priceRow('1回のお届け分の商品代金', plan.perDeliveryMoney);
        if (plan.hasDifferentCheckoutCharge) this.priceRow('今回決済する商品代金', plan.checkoutChargeMoney);
        if (plan.hasRemainingBalance) this.priceRow('後日決済する残額', plan.remainingBalanceMoney);
      }
      const reason = this.blockReason();
      this.text('[data-order-status]', reason || (subscription
        ? `選んだ内訳：${this.flavorState().summary}。定期購入条件をご確認のうえ、お進みください。`
        : `選んだ内訳：${this.flavorState().summary}。1セットをカートに追加します。`));
      this.variantInput.value = variant?.id || '';
      this.variantInput.disabled = Boolean(reason);
      this.planInput.value = plan?.id || '';
      this.planInput.disabled = Boolean(reason) || !subscription || !plan;
      this.button.disabled = Boolean(reason) || Boolean(this.submitting);
      this.button.textContent = this.submitting ? 'カートに追加しています…' : reason
        ? (!variant?.available && variant ? '売り切れ' : !this.flavorState().valid ? '味の本数を調整してください' : '現在お申し込みいただけません')
        : subscription ? '定期購入で1セットをカートに入れる' : '1セットをカートに入れる';
      if (!this.currentSet?.productId) this.button.textContent = 'セット準備中';
    }

    submit(event) {
      // Re-evaluate against the selected variant and its own allocations.
      if (this.submitting || this.blockReason()) {
        event.preventDefault();
        this.render();
        return;
      }
      this.render();
      this.submitting = true;
      this.button.disabled = true;
      this.button.textContent = 'カートに追加しています…';
      // Native, locale-aware cart/add POST. Shopify validates inventory and
      // prices for the bundle variant. Flavor counts are visible line-item
      // properties, not component inventory. Only a selected subscription
      // sends a selling_plan field. Fulfillment must read these properties.
    }
  }

  customElements.define('chura-custom-order', ChuraCustomOrder);
})();
